import { useEffect, useState } from 'react'

const MAX_DESC = 1000

/** 클러스터링 프로젝트 생성.
 *
 * 마크업·색은 학습 데이터셋의 `CreateDatasetProjectModal` 과 같은 관행을 따른다
 * (주요 버튼 bg-gray-800, 보조 버튼 border-gray-300, gray 계열).
 */
export default function CreateClusteringProjectModal({
  isOpen, onClose, onSubmit,
}: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (name: string, description: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setName(''); setDescription(''); setError(null); setSubmitting(false)
  }, [isOpen])

  if (!isOpen) return null

  const isValid = name.trim().length > 0

  const handleSubmit = async () => {
    if (!isValid || submitting) return
    setError(null)
    setSubmitting(true)
    try {
      await onSubmit(name.trim(), description.trim())
    } catch (e: any) {
      setError(e?.message || '생성 중 오류가 발생했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <div className="text-lg font-semibold mb-2">클러스터링 프로젝트 생성</div>
        <div className="text-xs text-gray-500 mb-4">
          프로젝트를 만든 뒤 「클러스터링 생성」에서 대상 원천데이터 폴더를 고릅니다.
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-800 mb-1">
              프로젝트명 <span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={255}
              disabled={submitting}
              placeholder="예: 야간 주행 영상 분포 분석"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-800 mb-1">프로젝트 설명 (선택)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              maxLength={MAX_DESC}
              disabled={submitting}
              placeholder="이 프로젝트에서 무엇을 확인하려는지"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
            onClick={onClose}
            disabled={submitting}
          >취소</button>
          <button
            className={`px-3 py-1 text-sm rounded text-white ${
              isValid && !submitting ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'
            }`}
            disabled={!isValid || submitting}
            onClick={handleSubmit}
          >
            {submitting ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </div>
  )
}
